import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShieldCheck, FileText, ArrowLeft, Trash2 } from 'lucide-react';
import { CONTACT } from '../lib/constants';

const Section = ({ title, children }) => (
  <section className="mt-8">
    <h2 className="text-lg font-extrabold text-[#1E2130]">{title}</h2>
    <div className="mt-2 space-y-2 text-[15px] leading-relaxed text-gray-600">{children}</div>
  </section>
);

const PRIVACY = (
  <>
    <Section title="Qué información recopilamos">
      <p>Para poder entregarte un pedido necesitamos: tu nombre, tu número de teléfono, tu correo electrónico (si lo proporcionas o si entras con Google), tus direcciones de entrega con su ubicación en el mapa, el historial de tus pedidos y los comprobantes de pago que subes.</p>
      <p>Si eres conductor, además guardamos tu cédula, la foto de tu documento, la foto de tu vehículo con su placa y tu ubicación mientras tienes un pedido activo.</p>
    </Section>
    <Section title="Para qué la usamos">
      <p>Únicamente para operar el servicio: preparar y entregar tus pedidos, permitir que el conductor te llame o te escriba, verificar los pagos por transferencia, mostrarte el estado de tu pedido y atenderte cuando escribes a soporte.</p>
      <p>No vendemos ni alquilamos tus datos a nadie. No los usamos para publicidad de terceros.</p>
    </Section>
    <Section title="Con quién la compartimos">
      <p>Compartimos solo lo indispensable: el negocio recibe el detalle de tu pedido y el conductor recibe tu nombre, tu teléfono y tu dirección de entrega para poder llegar. Nada más.</p>
    </Section>
    <Section title="Ubicación y permisos del teléfono">
      <p>La aplicación pide acceso a tu <b>ubicación</b> para calcular la tarifa de tu zona y guiar al conductor, a la <b>cámara y tus fotos</b> para que puedas subir comprobantes de pago o fotos de entrega, y a las <b>notificaciones</b> para avisarte cuando tu pedido cambia de estado. Puedes negar o retirar cualquiera de estos permisos desde los ajustes de tu teléfono; la app seguirá funcionando con funciones limitadas.</p>
    </Section>
    <Section title="Cuánto tiempo guardamos tus datos">
      <p>Conservamos tu cuenta y tu historial mientras uses el servicio. Los comprobantes de pago y las fotos de entrega se conservan para resolver reclamos. Si pides que borremos tu cuenta, eliminamos tus datos personales salvo los registros contables que debamos conservar por ley.</p>
    </Section>
    <Section title="Seguridad">
      <p>Tu contraseña se guarda cifrada y nunca es visible para nosotros. La conexión con la aplicación siempre va cifrada (HTTPS).</p>
    </Section>
    <Section title="Eliminar tu cuenta o tus datos">
      <p>Puedes borrar tu cuenta tú mismo desde la app: <b>Perfil → Eliminar mi cuenta</b>. También puedes escribirnos por WhatsApp al {CONTACT.whatsapp} o al correo <b>{CONTACT.email}</b> y la procesamos sin costo. El detalle de qué se borra y qué se conserva está en <Link to="/eliminar-cuenta" className="text-[#EE3B36] font-bold">esta página</Link>.</p>
    </Section>
    <Section title="Menores de edad">
      <p>El servicio está dirigido a personas mayores de 18 años o a menores con autorización de su madre, padre o tutor.</p>
    </Section>
    <Section title="Cambios y contacto">
      <p>Si actualizamos esta política publicaremos la nueva versión en esta misma página. Para cualquier duda sobre tus datos: WhatsApp {CONTACT.whatsapp} · {CONTACT.email}</p>
    </Section>
  </>
);

const TERMS = (
  <>
    <Section title="Qué es Delibery 7">
      <p>Delibery 7 es un servicio de entrega a domicilio y mandaditos en Tocoa, Colón, Honduras. Conectamos a clientes con negocios locales y con conductores repartidores.</p>
    </Section>
    <Section title="Pedidos y precios">
      <p>Los precios de los productos los define cada negocio. El costo de envío depende del barrio o colonia de entrega, y en las encomiendas depende de la distancia. El total siempre se te muestra antes de confirmar.</p>
    </Section>
    <Section title="Pagos">
      <p>Puedes pagar en efectivo al recibir, o por transferencia bancaria subiendo tu comprobante. Los pedidos con transferencia se preparan únicamente después de verificar el pago. Si eliges recoger en tienda, la transferencia va a la cuenta del negocio y es el negocio quien verifica el comprobante.</p>
    </Section>
    <Section title="Cancelaciones y reembolsos">
      <p>Puedes cancelar mientras el negocio no haya empezado a preparar tu pedido. Si ya está en preparación o en camino, la cancelación queda a criterio del negocio. Si pagaste por transferencia y el pedido se cancela por causas nuestras o del negocio, se te devuelve el monto completo.</p>
    </Section>
    <Section title="Tiempos de entrega">
      <p>Los tiempos que se muestran son estimados y pueden variar por clima, tráfico, disponibilidad de conductores o demanda del negocio.</p>
    </Section>
    <Section title="Uso correcto de la aplicación">
      <p>No se permite crear pedidos falsos, suplantar a otra persona, subir comprobantes alterados ni maltratar a los conductores o al personal de los negocios. Podemos desactivar cuentas que incumplan estas reglas.</p>
    </Section>
    <Section title="Conductores y negocios">
      <p>Los conductores son repartidores independientes y los negocios son responsables de la calidad, higiene y preparación de sus productos. Delibery 7 gestiona la entrega y la plataforma.</p>
    </Section>
    <Section title="Contacto">
      <p>WhatsApp {CONTACT.whatsapp} · {CONTACT.email}</p>
    </Section>
  </>
);

const DELETE_ACCOUNT = (
  <>
    <Section title="Desde la aplicación (inmediato)">
      <p>Abre la app de <b>Delibery 7</b> → pestaña <b>Perfil</b> → botón <b>«Eliminar mi cuenta»</b> al final de la pantalla. Te pedimos tu contraseña para confirmar y la cuenta se elimina de inmediato, sin pasar por soporte.</p>
    </Section>
    <Section title="Si ya no tienes la app instalada">
      <p>Escríbenos por WhatsApp al <b>{CONTACT.whatsapp}</b> o al correo <b>{CONTACT.email}</b> desde el número o el correo con el que te registraste, pidiendo la eliminación de tu cuenta. La procesamos sin costo en un máximo de <b>30 días</b> (normalmente el mismo día).</p>
    </Section>
    <Section title="Qué se elimina">
      <p>Tu cuenta y tu contraseña, tu nombre, tu teléfono y tu correo, todas tus direcciones guardadas y sus ubicaciones en el mapa, tu foto de perfil, tus favoritos, tus notificaciones y el registro de tus dispositivos para notificaciones. Tus mensajes de chat quedan sin tu nombre.</p>
    </Section>
    <Section title="Qué conservamos y por qué">
      <p>Conservamos el registro contable de los pedidos que ya se cobraron (número de pedido, fecha, productos y montos) <b>sin tus datos personales</b>: el nombre y la dirección se reemplazan por «Cuenta eliminada». Esto es obligatorio para la contabilidad del negocio y para resolver reclamos o disputas de pago. Los comprobantes de transferencia y las fotos de evidencia de entrega de pedidos ya cobrados también se conservan por el mismo motivo.</p>
      <p>Después de eliminar tu cuenta no podemos recuperarla ni devolverte tu historial. Puedes registrarte de nuevo cuando quieras, empezando desde cero.</p>
    </Section>
    <Section title="Contacto">
      <p>WhatsApp {CONTACT.whatsapp} · {CONTACT.email}</p>
    </Section>
  </>
);

export const PAGES = {
  privacidad: { key: 'privacy-page', icon: ShieldCheck, title: 'Política de privacidad', body: PRIVACY },
  terminos: { key: 'terms-page', icon: FileText, title: 'Términos y condiciones', body: TERMS },
  'eliminar-cuenta': { key: 'delete-account-page', icon: Trash2, title: 'Eliminar tu cuenta y tus datos', body: DELETE_ACCOUNT },
};

const Legal = () => {
  const slug = useLocation().pathname.replace(/^\/+|\/+$/g, '') || 'privacidad';
  const page = PAGES[slug] || PAGES.privacidad;
  const Icon = page.icon;
  return (
    <div className="min-h-screen bg-white" data-testid={page.key}>
      <div className="bg-[#120b0b] text-white px-6 py-10">
        <div className="max-w-2xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-white/60 hover:text-white mb-6"><ArrowLeft size={15} /> Volver al inicio</Link>
          <div className="flex items-center gap-2 text-[#ef8f8c] text-xs font-bold tracking-[0.25em] uppercase">
            <Icon size={14} /> Delibery 7
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold mt-3">{page.title}</h1>
          <p className="text-sm text-white/50 mt-2">Última actualización: 1 de octubre de 2026</p>
        </div>
      </div>
      <div className="max-w-2xl mx-auto px-6 pb-20">
        {page.body}
        <div className="mt-10 pt-6 border-t border-gray-100 flex flex-wrap gap-x-6 gap-y-2 text-sm font-bold text-[#EE3B36]">
          {Object.entries(PAGES).filter(([s]) => s !== slug).map(([s, p]) => (
            <Link key={s} to={`/${s}`} data-testid={`legal-link-${s}`}>{p.title}</Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Legal;
